import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeftIcon, LinkIcon } from '@heroicons/react/24/outline';
import {
  Button, Card, Container, Eyebrow, Page, PageHeader, PlatformLogo, Subtitle, Title,
} from '@monorepo/common/components/ui';
import { useTikTok } from '../context/TikTokContext';
import { useInstagram } from '../context/InstagramContext';
import { useFacebook } from '../context/FacebookContext';
import { useThreads } from '../context/ThreadsContext';
import { useTwitter } from '../context/TwitterContext';
import AddAccountModal from '../components/AddAccountModal';
import InstagramConnectChooser from '../components/InstagramConnectChooser';

/**
 * Pick a platform and start its OAuth flow.
 *
 * ### This is a page, not a modal
 *
 * It looks like a modal's job — pick one thing, come back — and it was written
 * as a dead end, which is the worst of both: modal behaviour without a modal's
 * way out. But it cannot actually BE one. OAuth leaves the app entirely and
 * comes back to a callback URL, and the connect chooser and the "log out
 * first" notice are already dialogs ON this screen; a dialog over a dialog on
 * a phone is a stack nobody can back out of. A route survives the round trip,
 * a modal does not.
 *
 * So it stays a page and gains what it was missing: a way out, in the header,
 * where every other page in the app puts its actions.
 *
 * ### One table, five platforms
 *
 * These were five hand-written blocks of near-identical JSX, differing in a
 * logo, two strings and a permission — which is how one of them ends up with a
 * hover colour the others do not have (it did). The order is deliberate:
 * it matches the platform order used on the dashboard and in the composer, so
 * the same five things are never in three different orders.
 */
const PLATFORMS = [
  { key: 'tiktok', name: 'TikTok', blurb: 'Post videos and view analytics', permission: 'manage_tiktok_accounts' },
  { key: 'instagram', name: 'Instagram', blurb: 'Profile stats, posts and analytics', permission: 'manage_instagram' },
  { key: 'facebook', name: 'Facebook Pages', blurb: 'Publish to a Page and read its stats', permission: 'manage_facebook' },
  { key: 'threads', name: 'Threads', blurb: 'Post threads and view your profile', permission: 'manage_threads' },
  { key: 'twitter', name: 'X', blurb: 'Post and view analytics', permission: 'manage_twitter' },
];

const ConnectAccounts = () => {
  const navigate = useNavigate();
  const { connectTikTok, accounts: tiktokAccounts } = useTikTok();
  const { connectInstagram, connectInstagramViaFacebook, accounts: instagramAccounts } = useInstagram();
  const { connectFacebook, accounts: facebookAccounts } = useFacebook();
  const { connectThreads, accounts: threadsAccounts } = useThreads();
  const { connectTwitter, accounts: twitterAccounts } = useTwitter();
  const permissions = useSelector((state) => state.auth.permissions || []);

  // Which platform's "add another" pre-connect modal is open (null = none).
  const [modalPlatform, setModalPlatform] = useState(null);
  // Instagram asks how to connect before launching OAuth, since the choice
  // decides which metrics exist for the account afterwards.
  const [igChooserOpen, setIgChooserOpen] = useState(false);

  const connectFns = {
    tiktok: connectTikTok,
    instagram: connectInstagram,
    facebook: connectFacebook,
    threads: connectThreads,
    twitter: connectTwitter,
  };

  // Multiple accounts per platform are supported per-org, so a platform is
  // never disabled — it shows the count and offers "add another". Per-account
  // management lives on each account's own overview page.
  const counts = {
    tiktok: tiktokAccounts.length,
    instagram: instagramAccounts.length,
    facebook: facebookAccounts.length,
    threads: threadsAccounts.length,
    twitter: twitterAccounts.length,
  };

  // Clicking a platform: with ≥1 account already there, show the "log out
  // first to add a different account" notice; its Continue then launches
  // OAuth. A first connect goes straight there.
  const handleConnect = (platform) => {
    if (counts[platform] > 0) {
      setModalPlatform(platform);
    } else if (platform === 'instagram') {
      setIgChooserOpen(true);
    } else {
      connectFns[platform]?.();
    }
  };

  const handleModalContinue = () => {
    const platform = modalPlatform;
    setModalPlatform(null);
    if (platform === 'instagram') {
      setIgChooserOpen(true);
      return;
    }
    connectFns[platform]?.();
  };

  const visible = PLATFORMS.filter((p) => permissions.includes('admin') || permissions.includes(p.permission));

  return (
    <Page>
      {/* `narrow`, not a max-width in className: cn() does not merge Tailwind
          conflicts, so two max-w utilities would resolve by stylesheet order
          rather than by which one was written last. */}
      <Container narrow>
        <PageHeader>
          <div className="min-w-0">
            <Eyebrow>
              <LinkIcon aria-hidden="true" />
              Connect
            </Eyebrow>
            <Title>Connect an account</Title>
            <Subtitle>Choose a platform to connect to your organization.</Subtitle>
          </div>
          {/* The way out. `navigate(-1)` returns you to whatever sent you here
              — the dashboard, an empty state, the composer — which is the
              thing a modal's close button does and this screen had no
              equivalent of at all. */}
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
            <ArrowLeftIcon aria-hidden="true" className="size-4" />
            Done
          </Button>
        </PageHeader>

        <InstagramConnectChooser
          open={igChooserOpen}
          onChooseFull={() => { setIgChooserOpen(false); connectInstagramViaFacebook(); }}
          onChooseBasic={() => { setIgChooserOpen(false); connectInstagram(); }}
          onClose={() => setIgChooserOpen(false)}
        />

        <AddAccountModal
          open={modalPlatform !== null}
          platform={modalPlatform}
          variant="pre"
          onContinue={handleModalContinue}
          onClose={() => setModalPlatform(null)}
        />

        <div className="grid gap-2.5">
          {visible.map((p) => (
            <Card
              key={p.key}
              as="button"
              type="button"
              interactive
              onClick={() => handleConnect(p.key)}
              className="flex w-full items-center gap-3 text-left"
            >
              {/* Brand colour, here and only here: choosing a platform IS the
                  task on this screen, which is the one place the design system
                  sanctions it. Everywhere else the same mark is monochrome. */}
              <span className="flex size-11 shrink-0 items-center justify-center rounded-card bg-surface-raised">
                <PlatformLogo platform={p.key} tone="brand" label={null} className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body font-medium text-ink">{p.name}</span>
                <span className="block truncate text-body-sm text-ink-muted">{p.blurb}</span>
              </span>
              <span className="shrink-0 text-right">
                {counts[p.key] > 0 ? (
                  <>
                    <span className="block text-caption text-success">{counts[p.key]} connected</span>
                    <span className="block text-caption-sm text-ink-subtle">Add another →</span>
                  </>
                ) : (
                  <span className="text-caption text-ink-muted">Connect →</span>
                )}
              </span>
            </Card>
          ))}

          {/* Not yet supported, and said once rather than as a row per
              platform: a list of things you cannot do is not a feature. */}
          <p className="mt-1 text-caption text-ink-subtle">YouTube is not supported yet.</p>
        </div>

        {/* A second way out, at the end of the list — on a phone the header has
            scrolled away by the time you have read five options and decided
            you did not want any of them. */}
        <div className="mt-6">
          <Button as={Link} to="/dashboard" variant="ghost" size="sm">
            Back to accounts
          </Button>
        </div>
      </Container>
    </Page>
  );
};

export default ConnectAccounts;
